// @ts-strict-ignore
import { crush } from '@tedconf/js-crushinator-helpers';
import { Video } from 'api';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { LazyLoadAd, TopicPageAds } from 'components/ads';
import Grid from 'components/grid';
import Col from 'components/grid/col';
import { Link } from 'components/router';
import SkipLink from 'components/skip-link';
import TalkCard from 'components/talk-card';
import { getBadgeText } from 'components/talk-card/utils';
import { Heading, Text } from 'components/typography';
import { relativeTedUrl } from 'lib/pages/homepage/utils';

type TopicTalksProps = {
  name: string;
  slug: string;
  skipLink: string;
  talks: (Video | null)[];
  totalCount: number;
};

function TopicTalks({
  name,
  slug,
  skipLink,
  totalCount,
  talks
}: TopicTalksProps): React.ReactNode {
  // Filter out null values and ensure talks have required data
  const validTalks = talks.filter(
    (talk): talk is Video => talk !== null && !!talk.primaryImageSet?.[0]?.url
  );

  const firstChunkOfTalks = validTalks.slice(0, 8);
  const secondChunkOfTalks = validTalks.slice(8);

  return (
    <section id="talks" tabIndex={-1}>
      {skipLink && (
        <SkipLink to={skipLink}>
          <FormattedMessage defaultMessage="Skip Talks" />
        </SkipLink>
      )}
      <Heading size="l" className="mb-8">
        <FormattedMessage
          defaultMessage="Talks about {topicName}"
          values={{ topicName: name }}
        />
      </Heading>
      <Grid eightColGrid>
        {firstChunkOfTalks.map(talk => {
          const image = crush(talk.primaryImageSet?.[0].url, { width: 600 });
          const badgeText = getBadgeText(talk.type?.name);

          return (
            <Col sm={2} md={2} lg={2} xl={3} key={talk.slug}>
              <TalkCard
                className="mb-8"
                href={relativeTedUrl(talk.canonicalUrl)}
                imageUrl={image.toString()}
                title={talk.title}
                duration={talk.duration}
                presenterDisplayName={talk.presenterDisplayName}
                badgeText={badgeText}
              />
            </Col>
          );
        })}
      </Grid>
      <LazyLoadAd>
        <TopicPageAds.TopicResultsAdsInlineBTF />
      </LazyLoadAd>
      <Grid eightColGrid>
        {secondChunkOfTalks.map(talk => {
          const image = crush(talk.primaryImageSet?.[0].url, { width: 600 });
          const badgeText = getBadgeText(talk.type?.name);
          return (
            <Col sm={2} md={2} lg={2} xl={3} key={talk.slug}>
              <TalkCard
                className="mb-8"
                href={relativeTedUrl(talk.canonicalUrl)}
                imageUrl={image.toString()}
                title={talk.title}
                duration={talk.duration}
                presenterDisplayName={talk.presenterDisplayName}
                badgeText={badgeText}
              />
            </Col>
          );
        })}
      </Grid>

      {totalCount > talks.length && (
        <Link href={`/talks?topics[0]=${slug}`}>
          <Text
            size="m"
            tagName="span"
            className="font-bold underline transition-colors hover:text-gray-700"
          >
            <FormattedMessage
              defaultMessage="See all talks on {topicName}"
              values={{ topicName: name }}
            />
          </Text>
        </Link>
      )}
    </section>
  );
}

export default TopicTalks;
